import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FileText, Table2, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList, Tooltip } from "recharts";
import api, { baseURL } from "~/services/api";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

const COLORS = { pass: "#3b82f6", fail: "#f97316" };

const CustomShiftTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;

  return (
    <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
      <p className="mb-1 font-semibold text-sm">{d.name}</p>
      <p className="text-sm">
        <span className="font-medium">{(d.count ?? 0).toLocaleString()}</span> students
      </p>
    </div>
  );
};

const InactiveTooltip = ({ course }) => {
  return (
    <div className="absolute z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-xs whitespace-nowrap pointer-events-none" style={{ left: '100%', top: '50%', transform: 'translate(8px, -50%)', minWidth: '180px' }}>
      <div className="font-semibold mb-2 text-gray-700 border-b pb-1">Inactive Students: {course.inactive_students}</div>
      <div className="flex justify-between py-1">
        <span className="text-gray-600">Vacation:</span>
        <span className="font-semibold text-gray-800">{course.inactive_vacation}</span>
      </div>
      <div className="flex justify-between py-1">
        <span className="text-gray-600">Medical Excuse:</span>
        <span className="font-semibold text-gray-800">{course.inactive_medical_excuse}</span>
      </div>
    </div>
  );
};

export default function AcademicDashboard() {
  const filial = useSelector((state) => state.auth?.filial);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedTeacher, setSelectedTeacher] = useState("all");
  const [selectedShift, setSelectedShift] = useState("all");
  const [considerInactive, setConsiderInactive] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);
  const [shiftData, setShiftData] = useState([]);
  const [totalStats, setTotalStats] = useState({
    studentActiveCount: 0,
    totalPass: 0,
    totalFail: 0,
    totalPassPercent: 0,
    totalFailPercent: 0,
    totalStudentCount: 0,
    inactiveStudents: 0,
  });
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  async function handleExportExcel() {
    if (!periodFrom || !periodTo) {
      toast("Por favor, selecione o período antes de exportar", { type: "warning", autoClose: 2500 });
      return;
    }

    setExportingExcel(true);
    try {
      const params = {
        period_from: periodFrom,
        period_to: periodTo,
        consider_inactive: considerInactive,
      };

      if (selectedGroup !== "all") params.group_id = selectedGroup;
      if (selectedTeacher !== "all") params.staff_id = selectedTeacher;
      if (selectedShift !== "all") params.shift = selectedShift;

      const { data } = await api.get("/passfail/dashboard/excel", { params });
      const name = data?.name || `academic_dashboard_${periodFrom}_to_${periodTo}.xlsx`;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel gerado com sucesso!", { type: "success", autoClose: 2500 });
    } catch (err) {
      toast(err?.response?.data?.error || "Falha ao gerar Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  }

  async function handleExportPDF() {
    if (!periodFrom || !periodTo) {
      toast("Por favor, selecione o período antes de exportar", { type: "warning", autoClose: 2500 });
      return;
    }

    setExportingPDF(true);
    try {
      const params = {
        period_from: periodFrom,
        period_to: periodTo,
        consider_inactive: considerInactive,
      };

      if (selectedGroup !== "all") params.group_id = selectedGroup;
      if (selectedTeacher !== "all") params.staff_id = selectedTeacher;
      if (selectedShift !== "all") params.shift = selectedShift;

      const { data } = await api.get("/passfail/dashboard/pdf", {
        params,
        responseType: "blob"
      });

      const pdfBlob = new Blob([data], { type: "application/pdf" });
      const name = `academic_dashboard_${periodFrom}_to_${periodTo}.pdf`;
      saveAs(pdfBlob, name);
      toast("PDF gerado com sucesso!", { type: "success", autoClose: 2500 });
    } catch (err) {
      toast(err?.response?.data?.error || "Falha ao gerar PDF", { type: "error", autoClose: 3000 });
    } finally {
      setExportingPDF(false);
    }
  }

  async function fetchDashboardData() {
    if (!periodFrom || !periodTo) return;

    setLoading(true);
    try {
      const params = {
        period_from: periodFrom,
        period_to: periodTo,
        consider_inactive: considerInactive,
      };

      if (selectedGroup !== "all") params.group_id = selectedGroup;
      if (selectedTeacher !== "all") params.staff_id = selectedTeacher;
      if (selectedShift !== "all") params.shift = selectedShift;

      const { data } = await api.get("/passfail/dashboard", { params });

      setDashboardData(data.courses || []);
      setShiftData(data.shifts || []);
      setTotalStats({
        studentActiveCount: data.total_active || 0,
        totalPass: data.total_pass || 0,
        totalFail: data.total_fail || 0,
        totalPassPercent: data.total_pass_percent || 0,
        totalFailPercent: data.total_fail_percent || 0,
        totalStudentCount: data.total_registered || 0,
        inactiveStudents: data.total_inactive || 0,
      });

      if (data.filter_options) {
        setGroups(data.filter_options.groups || []);
        setTeachers(data.filter_options.teachers || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardData([]);
      setShiftData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDashboardData();
  }, [periodFrom, periodTo, selectedGroup, selectedTeacher, selectedShift, considerInactive, filial?.id]);

  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        className="font-bold text-sm"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CourseChart = ({ course }) => {
    const baseTotal = considerInactive ? course.students_registered : course.students_active;
    const passPercent = calculatePercentage(course.pass, baseTotal);
    const failPercent = calculatePercentage(course.fail, baseTotal);
    const data = [
      { name: "Pass", value: passPercent },
      { name: "Fail", value: failPercent },
    ];

    return (
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <h3 className="text-center font-bold text-gray-800 mb-2">{course.level}</h3>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={70}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.name === "Pass" ? COLORS.pass : COLORS.fail} />
              ))}
            </Pie>
            <Legend verticalAlign="bottom" height={36} iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const OverallChart = () => {
    const data = [
      { name: "Pass", value: totalStats.totalPassPercent },
      { name: "Fail", value: totalStats.totalFailPercent },
    ];

    return (
      <div className="bg-white border rounded-lg p-4 shadow-sm" style={{ gridRow: "span 2" }}>
        <h3 className="text-center font-bold text-gray-800 mb-2 text-lg">Overall</h3>
        <ResponsiveContainer width="100%" height="90%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.name === "Pass" ? COLORS.pass : COLORS.fail} />
              ))}
            </Pie>
            <Legend verticalAlign="bottom" height={36} iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const mbe2Index = dashboardData.findIndex((c) => c.level === "MBE2");
  const mbe2Course =
    mbe2Index >= 0 && mbe2Index > 5
      ? dashboardData[mbe2Index]
      : mbe2Index === -1
        ? dashboardData[6]
        : null;

  return (
    <div className="w-full h-full flex flex-col bg-white border overflow-hidden rounded-r-xl">
      <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-gray-700" />
          <h2 className="text-lg font-bold text-gray-700">Pass and Fail Dashboard</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            disabled={exportingExcel || !periodFrom || !periodTo}
            className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-3 py-1.5 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exportingExcel ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Table2 size={16} />
            )}{" "}
            Generate Worksheet
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exportingPDF || !periodFrom || !periodTo}
            className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-3 py-1.5 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exportingPDF ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <FileText size={16} />
            )}{" "}
            PDF
          </button>
        </div>
      </div>

      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Period from / to</label>
          <div className="flex gap-2">
            <input
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            />
            <input
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Group</label>
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48"
          >
            <option value="all">All Groups</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Teacher</label>
          <select
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48"
          >
            <option value="all">All Teachers</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Shift</label>
          <select
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48"
          >
            <option value="all">All Shifts</option>
            <option value="morning">Morning</option>
            <option value="morning/afternoon">Morning/Afternoon</option>
            <option value="afternoon">Afternoon</option>
            <option value="evening">Evening</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Options</label>
          <label className="flex items-center gap-2 px-2 py-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={considerInactive}
              onChange={(e) => setConsiderInactive(e.target.checked)}
              className="w-4 h-4 text-mila_orange border-gray-300 rounded focus:ring-mila_orange focus:ring-1"
            />
            <span className="text-xs text-gray-700">Consider Inactive</span>
          </label>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="border rounded-sm mb-4 overflow-hidden">
          <div className="flex flex-row">
            <div className="bg-white p-5 border-r flex-shrink-0" style={{ width: "55%" }}>
              <h3 className="text-sm font-bold text-gray-700 mb-3">Students by Shift</h3>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={shiftData} margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
                  <Tooltip content={<CustomShiftTooltip />} cursor={{ fill: "transparent" }} />
                  <CartesianGrid vertical={false} strokeOpacity={0.15} />
                  <XAxis
                    dataKey="name"
                    fontSize={11}
                    fontWeight={600}
                    fill="#1f2937"
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                    angle={0}
                    textAnchor="middle"
                  />
                  <YAxis width={35} fontSize={11} tickFormatter={(v) => v.toLocaleString()} axisLine={false} tickLine={false} />
                  <Bar dataKey="count" radius={[8, 8, 8, 8]} fill="#EE5827">
                    <LabelList
                      dataKey="count"
                      content={(props) => {
                        const { x, y, width, height, value } = props;
                        const val = Number(value ?? 0);
                        const isLowBar = height < 25;
                        const yPos = isLowBar ? y - 8 : y + height - 12;

                        return (
                          <text
                            x={x + width / 2}
                            y={yPos}
                            textAnchor="middle"
                            className="text-[13px] font-semibold"
                            fill={isLowBar ? "#111827" : "#FFFFFF"}
                            pointerEvents="none"
                          >
                            {val.toLocaleString()}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white flex-1">
              <table className="w-full text-xs h-full">
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-semibold bg-gray-100 border-r">Total Pass:</td>
                    <td className="px-4 py-3 border-r">{totalStats.totalPass}</td>
                    <td className="px-4 py-3 font-semibold bg-gray-100 border-r">Total Student Count:</td>
                    <td className="px-4 py-3 border-r">{totalStats.totalStudentCount}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-semibold bg-gray-100 border-r">Total Fail:</td>
                    <td className="px-4 py-3 border-r">{totalStats.totalFail}</td>
                    <td className="px-4 py-3 font-semibold bg-gray-100 border-r">Inactive Students:</td>
                    <td className="px-4 py-3 border-r">{totalStats.inactiveStudents}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-semibold bg-gray-100 border-r">Total Percent Pass:</td>
                    <td className="px-4 py-3 border-r">{totalStats.totalPassPercent}%</td>
                    <td className="px-4 py-3 font-semibold bg-gray-100 border-r">Student Active Count:</td>
                    <td className="px-4 py-3 border-r">{totalStats.studentActiveCount}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-semibold bg-gray-100 border-r">Total Percent Fail:</td>
                    <td className="px-4 py-3 border-r">{totalStats.totalFailPercent}%</td>
                    <td className="px-4 py-3 bg-white"></td>
                    <td className="px-4 py-3 bg-white"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse min-w-max">
            <thead className="sticky top-0 bg-gray-200 z-10">
              <tr>
                <th className="px-2 py-2 text-xs font-semibold text-left border sticky left-0 bg-gray-200 z-20">Courses</th>
                <th className="px-2 py-2 text-xs font-semibold text-center border w-32">Students Active</th>
                <th className="px-2 py-2 text-xs font-semibold text-center border w-32">Students Registered</th>
                <th className="px-2 py-2 text-xs font-semibold text-center border w-32">Inactive Students</th>
                <th className="px-2 py-2 text-xs font-semibold text-center border w-24">Pass</th>
                <th className="px-2 py-2 text-xs font-semibold text-center border w-24">Fail</th>
                <th className="px-2 py-2 text-xs font-semibold text-center border w-24">Pass %</th>
                <th className="px-2 py-2 text-xs font-semibold text-center border w-24">Fail %</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-3 text-xs text-gray-500">Loading...</td></tr>
              ) : dashboardData.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-3 text-xs text-gray-500">No data available</td></tr>
              ) : (
                <>
                  {dashboardData.map((course, idx) => {
                    const baseTotal = considerInactive ? course.students_registered : course.students_active;
                    const passPercent = calculatePercentage(course.pass, baseTotal);
                    const failPercent = calculatePercentage(course.fail, baseTotal);
                    return (
                      <tr key={idx} className="bg-white hover:bg-gray-50">
                        <td
                          className="px-2 py-2 text-xs font-semibold sticky left-0 bg-white border relative"
                          onMouseEnter={() => setHoveredRow(idx)}
                          onMouseLeave={() => setHoveredRow(null)}
                        >
                          {course.level}
                          {hoveredRow === idx && course.inactive_students > 0 && (
                            <InactiveTooltip course={course} />
                          )}
                        </td>
                        <td className="px-2 py-2 text-xs text-center border">{course.students_active}</td>
                        <td className="px-2 py-2 text-xs text-center border">{course.students_registered}</td>
                        <td className="px-2 py-2 text-xs text-center border">{course.inactive_students}</td>
                        <td className="px-2 py-2 text-xs text-center border text-green-600 font-semibold">{course.pass}</td>
                        <td className="px-2 py-2 text-xs text-center border text-red-600 font-semibold">{course.fail}</td>
                        <td className="px-2 py-2 text-xs text-center border bg-green-50">{passPercent}%</td>
                        <td className="px-2 py-2 text-xs text-center border bg-red-50">{failPercent}%</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-2 py-2 text-xs sticky left-0 bg-gray-100 border">Overall</td>
                    <td className="px-2 py-2 text-xs text-center border">{totalStats.studentActiveCount}</td>
                    <td className="px-2 py-2 text-xs text-center border">{totalStats.totalStudentCount}</td>
                    <td className="px-2 py-2 text-xs text-center border">{totalStats.inactiveStudents}</td>
                    <td className="px-2 py-2 text-xs text-center border text-green-600">{totalStats.totalPass}</td>
                    <td className="px-2 py-2 text-xs text-center border text-red-600">{totalStats.totalFail}</td>
                    <td className="px-2 py-2 text-xs text-center border bg-green-50">{totalStats.totalPassPercent}%</td>
                    <td className="px-2 py-2 text-xs text-center border bg-red-50">{totalStats.totalFailPercent}%</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {dashboardData.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gridTemplateRows: "repeat(3, 1fr)",
              gap: "16px",
              minHeight: "400px",
            }}
          >
            {dashboardData[0] && <CourseChart course={dashboardData[0]} />}
            {dashboardData[1] && <CourseChart course={dashboardData[1]} />}
            {dashboardData[2] && <CourseChart course={dashboardData[2]} />}
            <OverallChart />
            {dashboardData[3] && <CourseChart course={dashboardData[3]} />}
            {dashboardData[4] && <CourseChart course={dashboardData[4]} />}
            {dashboardData[5] && <CourseChart course={dashboardData[5]} />}
            {mbe2Course && <CourseChart course={mbe2Course} />}
          </div>
        )}
      </div>
    </div>
  );
}